import React from 'react';
import { TrendingUpIcon, CheckIcon, AlertTriangleIcon, XIcon } from 'lucide-react';
import type { Division, MetricType, PeriodInfo } from '../../types';
import { aggregateDivisionMetrics, getProgressPercentage, getProgressStatus, calculateProjection } from '../../utils/calculations';
interface MetricSummaryProps {
  divisions: Division[];
  period: PeriodInfo;
  metric: MetricType;
}
export function MetricSummary({
  divisions,
  period,
  metric
}: MetricSummaryProps) {
  let totalCurrent = 0;
  let totalGoal = 0;
  let onTrackCount = 0;
  let atRiskCount = 0;
  let behindCount = 0;
  // Count per-LO statuses
  for (const division of divisions) {
    const divMetrics = aggregateDivisionMetrics(division, metric);
    totalCurrent += divMetrics.current;
    totalGoal += divMetrics.goal;
    for (const team of division.teams) {
      for (const lo of team.loanOfficers) {
        const current = metric === 'softPull' ? lo.softPullCount : lo.submittedCount;
        const goal = metric === 'softPull' ? lo.softPullGoal : lo.submittedGoal;
        const projected = calculateProjection(current, period.daysElapsed, period.totalDays);
        const projPct = getProgressPercentage(projected, goal);
        const status = getProgressStatus(projPct);
        if (status === 'on-track') onTrackCount++;else if (status === 'at-risk') atRiskCount++;else behindCount++;
      }
    }
  }
  const overallPct = getProgressPercentage(totalCurrent, totalGoal);
  const cards = [{
    label: 'Total Progress',
    value: `${totalCurrent} / ${totalGoal}`,
    sub: `${overallPct}% complete`,
    icon: <TrendingUpIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-87" />,
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800'
  }, {
    label: 'On Track',
    value: `${onTrackCount}`,
    sub: 'Loan Officers',
    icon: <CheckIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" data-id="element-88" />,
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800'
  }, {
    label: 'At Risk',
    value: `${atRiskCount}`,
    sub: 'Loan Officers',
    icon: <AlertTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" data-id="element-89" />,
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800'
  }, {
    label: 'Behind',
    value: `${behindCount}`,
    sub: 'Loan Officers',
    icon: <XIcon className="w-5 h-5 text-red-600 dark:text-red-400" data-id="element-90" />,
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800'
  }];
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-91">
      {cards.map(card => <div key={card.label} className={`rounded-lg border ${card.border} ${card.bg} p-4 transition-colors`} data-id="element-92">
          <div className="flex items-center justify-between mb-2" data-id="element-93">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300" data-id="element-94">
              {card.label}
            </span>
            <div className="w-8 h-8 rounded-lg bg-white dark:bg-gray-800 flex items-center justify-center shadow-sm" data-id="element-95">
              {card.icon}
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-96">
            {card.value}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-97">
            {card.sub}
          </p>
        </div>)}
    </div>;
}